/**
 * The basket, reviewed. Where the customer decides they are done.
 *
 * This screen carries the weight the menu's basket bar gave up: it is the only
 * place the whole order is visible line by line, so every line shows its
 * picture, what was chosen on it, and its own money.
 *
 * **A line is a slip, not a product card.** Name and money share a baseline —
 * that is how a receipt is read. Serving and quantity sit on the row below,
 * which is the row a hand can still change. Cup and bag sit on the words —
 * the marks alone were a puzzle at the counter. An extra is a chip, not a
 * footnote: it is what the barista has to make.
 *
 * **The summary is in the action bar, not in a card above it.** It was a dark
 * card at the foot of a column that grew from the top, so a two-line order —
 * which is most orders — left 900px of empty paper between the last item and
 * the total. Now the lines fill from the top, the money is the same bar the
 * menu had, and the breakdown lives behind a tap on the total.
 *
 * **An empty basket is a screen, not a disabled button.** The menu used to
 * refuse the cart at zero, so the only way to see "your basket is empty" was
 * to delete the last line — and then the way back to the menu was hidden. The
 * cart icon opens it; "add more" is the way out; Pay stays off.
 */
import { useState } from 'react'
import type { OrderType, PricedLine } from '@mgc/domain'
import { format, lineImage, lineLabel, lineOrderType, totalsOf } from '@mgc/domain'
import { Icon } from '@/components/icons'
import { ActionBar, BackButton, Button, ProductImage, Stepper, TemperatureTag, Total } from '@/components/ui'
import { RemovedNotice } from '@/components/removed-notice'
import { useT } from '@/i18n'
import { useKiosk, usePricedCart } from '@/store/kiosk'

export function Checkout() {
  const t = useT()
  const { lines, savings } = usePricedCart()
  const setStep = useKiosk((s) => s.setStep)
  const setQuantity = useKiosk((s) => s.setLineQuantity)
  const removeLine = useKiosk((s) => s.removeCartLine)
  const setLineOrder = useKiosk((s) => s.setLineOrder)
  const orderType = useKiosk((s) => s.cart.orderType)
  const locale = useKiosk((s) => s.locale)
  const [breakdown, setBreakdown] = useState(false)

  // The VAT split comes from the domain, not from a percentage written here.
  // The prototype this design is taken from multiplied the subtotal by 1.1 on
  // screen; the price on a Mongolian menu is already VAT-inclusive, so that
  // would have shown the customer a total 10% above what they were charged.
  const totals = totalsOf({ lines, payments: [] })
  const count = lines.reduce((n, line) => n + line.selection.quantity, 0)

  return (
    <div className="flex h-full flex-col bg-[var(--color-paper)]">
      <header className="flex min-h-[var(--band-head)] shrink-0 items-center justify-between gap-8 border-b border-[var(--color-line)] bg-[var(--color-paper-raised)] px-10 py-8">
        <h1 className="display truncate text-[length:var(--text-kiosk-xl)] uppercase">{t('cart.title')}</h1>
        <p className="display-upright shrink-0 text-[length:var(--text-kiosk-sm)] text-[var(--color-ink-faint)] uppercase">
          {count} {t('menu.items')}
        </p>
      </header>

      <div
        className={`min-h-0 flex-1 overflow-y-auto overscroll-contain px-10 py-8 ${
          lines.length === 0 ? 'flex flex-col' : 'space-y-5'
        }`}
      >
        {/*
          A catalogue push can drop a line while the customer is reading the
          basket. It is the same event the menu reports; it was reported only
          there, so the screen showing the total was the one screen that stayed
          quiet about the total changing.
        */}
        <RemovedNotice />
        {lines.length === 0 && (
          <div className="flex flex-1 flex-col items-center justify-center gap-8">
            <Icon name="cart" className="size-48 text-[var(--color-ink-ghost)]" />
            <p className="display text-[length:var(--text-kiosk-xl)] uppercase">{t('cart.empty')}</p>
            <Button size="large" className="uppercase" onClick={() => setStep('menu')}>
              <Icon name="plus" className="size-9" />
              {t('cart.addMore')}
            </Button>
          </div>
        )}

        {lines.map((line) => (
          <CartLine
            key={line.selection.id}
            line={line}
            cartType={orderType}
            locale={locale}
            eatIn={t('orderType.eatIn')}
            takeOut={t('orderType.takeOut')}
            eatInOnlyLabel={t('menu.eatInOnly')}
            hot={t('product.hot')}
            ice={t('product.ice')}
            removeLabel={t('cart.remove')}
            onQuantity={(n) => setQuantity(line.selection.id, n)}
            onRemove={() => removeLine(line.selection.id)}
            onServe={(kind) => setLineOrder(line.selection.id, kind === orderType ? undefined : kind)}
          />
        ))}

        {/*
          "Add more" as the last row in the list rather than a text link in the
          summary. It is the answer to "is that everything?", so it belongs at
          the end of the answer — as a quiet invitation, not a second empty card.
        */}
        {lines.length > 0 && (
          <button
            type="button"
            onClick={() => setStep('menu')}
            className="tap display-upright flex w-full items-center justify-center gap-4 rounded-[var(--radius-tile)] bg-[var(--color-paper-sunk)] py-8 text-[length:var(--text-kiosk)] text-[var(--color-ink-soft)] uppercase active:bg-[var(--color-brand-soft)] active:text-[var(--color-ink)]"
          >
            <Icon name="plus" className="size-9" />
            {t('cart.addMore')}
          </button>
        )}
      </div>

      {/*
        The breakdown, opened from the total rather than printed under it.
        `docs/data-model.md` keeps money as an integer at a declared scale and
        the VAT split has a fractional part, so this is the one place on the
        customer's path that shows tugrik with decimals — behind a deliberate
        tap, next to the number it explains, rather than as two permanently
        confusing lines above the pay button.
      */}
      {breakdown && (
        <dl className="z-20 shrink-0 space-y-3 border-t border-[var(--color-dark-line)] bg-[var(--color-dark)] px-10 pt-7 text-[length:var(--text-kiosk-sm)] text-white/45">
          <div className="flex justify-between gap-6">
            <dt>{t('cart.subtotal')}</dt>
            <dd className="text-white/75 tabular-nums">{format(totals.net)}</dd>
          </div>
          <div className="flex justify-between gap-6">
            <dt>{t('cart.vat')}</dt>
            <dd className="text-white/75 tabular-nums">{format(totals.vat)}</dd>
          </div>
          {savings > 0n && (
            <div className="flex justify-between gap-6 text-[var(--color-good)]">
              <dt>{t('cart.discount')}</dt>
              <dd className="tabular-nums">−{format(savings)}</dd>
            </div>
          )}
        </dl>
      )}

      <ActionBar>
        <BackButton label={t('common.back')} onClick={() => setStep('menu')} />

        <button
          type="button"
          onClick={() => setBreakdown((open) => !open)}
          aria-expanded={breakdown}
          aria-label={t('cart.summary')}
          className="tap flex min-w-0 flex-1 text-left"
        >
          {/*
            The total is the one number on this screen that moves, and every
            stepper press moves it. Announced politely and atomically, so it is
            read as one amount rather than as the digits that changed.
          */}
          <span role="status" aria-live="polite" aria-atomic="true" className="min-w-0 flex-1">
            <Total
              label={t('cart.total')}
              value={format(totals.gross)}
              hint={<Icon name={breakdown ? 'chevronDown' : 'chevronUp'} className="size-5" />}
            />
          </span>
        </button>

        <Button
          size="large"
          onDark
          className="shrink-0 uppercase"
          disabled={lines.length === 0}
          onClick={() => setStep('payment')}
        >
          {t('cart.checkout')}
          <Icon name="forward" className="size-9" />
        </Button>
      </ActionBar>
    </div>
  )
}

function CartLine({
  line,
  cartType,
  locale,
  eatIn,
  takeOut,
  eatInOnlyLabel,
  hot,
  ice,
  removeLabel,
  onQuantity,
  onRemove,
  onServe
}: {
  line: PricedLine
  cartType: OrderType | null
  locale: 'mn' | 'en'
  eatIn: string
  takeOut: string
  eatInOnlyLabel: string
  hot: string
  ice: string
  removeLabel: string
  onQuantity: (n: number) => void
  onRemove: () => void
  onServe: (kind: OrderType) => void
}) {
  const served = lineOrderType(line.product, line.selection.orderType, cartType)
  const temperature = line.selection.temperature

  return (
    <div className="animate-fade-in flex gap-6 rounded-[var(--radius-tile)] border border-[var(--color-line)] bg-[var(--color-paper-raised)] p-6">
      <ProductImage
        src={lineImage(line)}
        name={line.product.name}
        className="aspect-[3/4] w-36 shrink-0 self-stretch rounded-[var(--radius-card)]"
      />

      <div className="flex min-w-0 flex-1 flex-col gap-5">
        <div className="flex items-start justify-between gap-6">
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              {temperature !== undefined && (
                <TemperatureTag
                  temperature={temperature}
                  label={temperature === 'ice' ? ice : hot}
                  className="shrink-0"
                />
              )}
              <p className="display-upright min-w-0 text-[length:var(--text-kiosk-lg)]">{lineLabel(line, locale)}</p>
            </div>
            {line.addOns.length > 0 && (
              <ul className="mt-3 flex flex-wrap gap-2">
                {line.addOns.map((item, index) => (
                  <li
                    key={`${item.addOn.code}-${index}`}
                    className="display-upright rounded-[var(--radius-pill)] bg-[var(--color-dark)] px-4 py-2 text-[length:var(--text-kiosk-sm)] text-[var(--color-brand)]"
                  >
                    {item.quantity > 1 ? `${item.addOn.label} ×${item.quantity}` : item.addOn.label}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <span className="display shrink-0 text-[length:var(--text-kiosk-lg)] tabular-nums">{format(line.total)}</span>
        </div>

        <div className="mt-auto flex items-center gap-5">
          <ServingSwitch
            value={served}
            eatInOnly={line.product.onlyTakeIn}
            eatIn={eatIn}
            takeOut={takeOut}
            eatInOnlyLabel={eatInOnlyLabel}
            onChange={onServe}
          />
          <div className="flex shrink-0 items-center gap-3">
            <Stepper quantity={line.selection.quantity} onChange={onQuantity} />
            <button
              type="button"
              onClick={onRemove}
              aria-label={removeLabel}
              className="tap grid size-18 min-h-0 shrink-0 place-items-center rounded-full bg-[var(--color-bad)]/10 text-[var(--color-bad)]"
            >
              <Icon name="close" className="size-9" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * How this line is served. The words are the control — cup and bag only
 * name them. An eat-in-only drink is a fact, not a toggle.
 */
function ServingSwitch({
  value,
  eatInOnly,
  eatIn,
  takeOut,
  eatInOnlyLabel,
  onChange
}: {
  value: OrderType | null
  eatInOnly: boolean
  eatIn: string
  takeOut: string
  eatInOnlyLabel: string
  onChange: (kind: OrderType) => void
}) {
  if (eatInOnly) {
    return (
      <span className="display-upright inline-flex min-h-[4.5rem] items-center gap-3 rounded-[var(--radius-pill)] bg-[var(--color-dark)] px-6 text-[length:var(--text-kiosk-sm)] text-[var(--color-brand)] uppercase">
        <Icon name="cup" className="size-7" />
        {eatInOnlyLabel}
      </span>
    )
  }

  return (
    <fieldset
      aria-label={`${eatIn} / ${takeOut}`}
      className="flex min-w-0 flex-1 rounded-[var(--radius-pill)] bg-[var(--color-paper-sunk)] p-1.5 ring-1 ring-[var(--color-line)]"
    >
      {(['eat_in', 'take_out'] as const).map((kind) => {
        const on = value === kind
        return (
          <button
            key={kind}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(kind)}
            className={`tap display-upright inline-flex flex-1 items-center justify-center gap-3 rounded-full px-6 text-[length:var(--text-kiosk-sm)] uppercase ${
              on ? 'bg-[var(--color-dark)] text-[var(--color-brand)]' : 'text-[var(--color-ink)]'
            }`}
          >
            <Icon name={kind === 'eat_in' ? 'cup' : 'bag'} className="size-7" />
            {kind === 'eat_in' ? eatIn : takeOut}
          </button>
        )
      })}
    </fieldset>
  )
}
